/**
 * Typed Worker fetches. All status/command calls use App Bridge JWTs.
 * API contract: apps/shopify/API_CONTRACT.md
 */
import type {
  Command,
  CommandType,
  StatusBlob,
} from "@erpclaw-shopify/shared";
import { WORKER_URL } from "../config";
import { getSessionToken } from "./sessionToken";

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly requestId: string | undefined;
  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

async function apiFetch<T>(
  path: string,
  init: RequestInit & { authed?: boolean } = {},
): Promise<T> {
  const { authed = true, headers: extraHeaders, ...rest } = init;
  const headers = new Headers(extraHeaders);
  if (authed) {
    const token = await getSessionToken();
    headers.set("authorization", `Bearer ${token}`);
  }
  if (rest.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const resp = await fetch(`${WORKER_URL}${path}`, { ...rest, headers });
  const text = await resp.text();
  let body: { ok?: boolean; data?: T; code?: string; title?: string; detail?: string; request_id?: string } = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    // leave body empty
  }
  if (!resp.ok) {
    throw new ApiError(
      resp.status,
      body.code ?? "UNKNOWN",
      body.detail ?? body.title ?? `HTTP ${resp.status}`,
      body.request_id,
    );
  }
  if (body.ok && body.data !== undefined) return body.data;
  return body as unknown as T;
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type StatusResponse =
  | { paired: false }
  | { paired: true; status: StatusBlob; last_updated_at: string };

export async function getStatus(shop: string): Promise<StatusResponse> {
  return apiFetch<StatusResponse>(`/status/${encodeURIComponent(shop)}`, {
    method: "GET",
  });
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export type CommandQueuedResponse = {
  command_id: string;
  queued_at: string;
  delivery_tier: "active" | "scheduled" | "on-demand";
};

export async function postCommand(
  shop: string,
  type: Exclude<CommandType, "gdpr-dispatch">,
  payload: Record<string, unknown> = {},
): Promise<CommandQueuedResponse> {
  return apiFetch<CommandQueuedResponse>(
    `/commands/${encodeURIComponent(shop)}`,
    {
      method: "POST",
      body: JSON.stringify({ type, payload }),
    },
  );
}

// ---------------------------------------------------------------------------
// Pair init (Token Exchange)
// ---------------------------------------------------------------------------

export type PairInitResponse = {
  pairing_code: string;
  expires_at: string;
  shop: string;
  scopes: string[];
};

/**
 * Mint a fresh pairing code via Shopify Token Exchange. The Worker
 * verifies the App Bridge session token, swaps it for an expiring
 * offline access token, encrypts everything into a PairingPayload,
 * and returns the 6-character code. Code TTL is 10 minutes.
 */
export async function pairInit(): Promise<PairInitResponse> {
  return apiFetch<PairInitResponse>("/pair/init", {
    method: "POST",
  });
}

// Re-export shared types so pages don't need both imports.
export type { Command, CommandType, StatusBlob };
